import AssignMenu from 'components/crm/ui/AssignMenu';
import Badge from 'components/badge/badge';
import CellButton from 'components/table/components/CellButton';
import EmptyCell from 'components/table/components/EmptyCell';
import TableCell from 'components/table/components/TableCell';
import TableRow from 'components/table/components/TableRow';
import Tooltips from 'components/tooltips/Tooltips';
import { recordOfLink, rowLinks } from 'helper/taskLinks';
import { ROW_LEAVE_CLASS } from 'hooks/useLeavingItems';
import useText from 'hooks/useText';
import LINK_ICONS from './linkIcons';
import TaskCheckbox from './TaskCheckbox';
import TaskTypeBadge from './TaskTypeBadge';

/**
 * A task in the table, a row of 56 px on the admin's TableRow and TableCell
 * (compact): it comes in with the rows' cascade, lights up when its assignee
 * or due day changes, turns green once ticked and fades out (`leaving`)
 * before it moves to the other tab. A callback ticked on the home page opens
 * nothing.
 */
const TaskTableRow = ({
  row,
  index,
  done,
  ticked,
  leaving,
  team,
  meId,
  onToggle,
  onOpen,
  onOpenCompany,
  onAssign,
  onLeaveEnd,
}) => {
  const { text } = useText('tasks');
  const opens = row.kind !== 'callback';
  // Rights come from the api; a row without them (the mock) keeps every gesture.
  const canEdit = row.access?.canEdit !== false;
  const canAssign = row.access?.canAssign !== false;
  // The first record of the task, then how many more: a company, group or
  // contact opens its page, a temp worker is only named.
  const links = rowLinks(row);
  const first = links[0];
  const target = first ? recordOfLink(first) : null;
  const FirstIcon = first ? LINK_ICONS[first.type] : null;
  return (
    <TableRow
      itemId={row.key}
      clickable={opens}
      onClick={opens ? () => onOpen(row) : undefined}
      className={`h-14 ${leaving ? ROW_LEAVE_CLASS : `motion-safe:animate-fadeUp ${ticked ? 'bg-green-50' : ''}`}`}
      style={leaving ? undefined : { animationDelay: `${Math.min(index, 9) * 30}ms` }}
      flashSignature={`${row.assigneeId}|${row.dueOn}`}
      onAnimationEnd={onLeaveEnd}
    >
      <TableCell compact position="first">
        <TaskCheckbox
          checked={done}
          disabled={!canEdit}
          label={text(done ? 'markUndone' : 'markDone')}
          onToggle={() => onToggle(row)}
        />
      </TableCell>
      <TableCell compact>
        <span className="flex min-w-0 items-center gap-1.5">
          <span
            className={`truncate font-medium transition-colors duration-300 ${
              done ? 'text-gray-400 line-through' : 'text-gray-900'
            }`}
          >
            {row.title}
          </span>
          {row.unreadComments && (
            <Tooltips text={text('unread')} placement="top">
              <span role="img" aria-label={text('unread')} className="block h-1.5 w-1.5 shrink-0 rounded-full bg-yellow-500" />
            </Tooltips>
          )}
        </span>
      </TableCell>
      <TableCell compact>
        <TaskTypeBadge row={row} />
      </TableCell>
      <TableCell compact>
        {first ? (
          <span className="flex min-w-0 items-center gap-1.5">
            {target ? (
              <CellButton icon={FirstIcon} tooltip={text('openCompany')} onClick={() => onOpenCompany(target)}>
                {first.label}
              </CellButton>
            ) : (
              <span className="-ml-1.5 flex min-w-0 items-center gap-3 px-1.5 py-1 text-gray-700">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-gray-100 bg-white">
                  <FirstIcon wh={16} color="currentColor" />
                </span>
                <span className="truncate">{first.label}</span>
              </span>
            )}
            {links.length > 1 && (
              <span className="shrink-0 text-xs text-gray-500">{text('moreLinks', { count: links.length - 1 })}</span>
            )}
          </span>
        ) : (
          <EmptyCell />
        )}
      </TableCell>
      <TableCell compact>
        {row.late && !done ? (
          <Badge type="ALERT">{row.dueLabel}</Badge>
        ) : (
          <span className="whitespace-nowrap text-gray-700">{row.dueLabel}</span>
        )}
      </TableCell>
      <TableCell compact>
        {row.timeLabel ? <span className="whitespace-nowrap text-gray-700">{row.timeLabel}</span> : <EmptyCell />}
      </TableCell>
      <TableCell compact>
        {row.kind === 'task' && (
          <span className="flex justify-end" onClick={(event) => event.stopPropagation()}>
            <AssignMenu
              value={row.assigneeId}
              people={team}
              meId={meId}
              readOnly={!canAssign}
              onChange={(assigneeId) => onAssign(row, assigneeId)}
            />
          </span>
        )}
      </TableCell>
      <TableCell compact position="last" />
    </TableRow>
  );
};

export default TaskTableRow;
